/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-25
 */

import React from 'react';
import { Chip, useTheme } from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTrialStatus } from '../../hooks/useTrialStatus';

interface TrialBadgeProps {
  /**
   * Size variant for the badge
   */
  size?: 'small' | 'medium';
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Whether the badge is clickable
   */
  clickable?: boolean;
  /**
   * Callback when badge is clicked
   */
  onClick?: () => void;
}

/**
 * Compact trial status badge for header/navigation display
 * Shows trial time remaining with color-coded urgency
 */
export const TrialBadge: React.FC<TrialBadgeProps> = ({
  size = 'small',
  className,
  clickable = false,
  onClick,
}) => {
  const { t } = useTranslation('billing');
  const theme = useTheme();
  const { isTrialActive, daysRemaining, warningLevel } = useTrialStatus();

  // Don't show badge if no trial active
  if (!isTrialActive) {
    return null;
  }

  // Get color and icon based on warning level
  const getBadgeProps = () => {
    switch (warningLevel) {
      case 'expired':
        return {
          color: theme.palette.error.main,
          backgroundColor: theme.palette.error.light,
          icon: <ErrorIcon sx={{ fontSize: 14 }} />,
          label: t('trial.expired'),
        };
      case 'urgent': // 1-3 days
        return {
          color: theme.palette.error.main,
          backgroundColor: theme.palette.error.light,
          icon: <WarningIcon sx={{ fontSize: 14 }} />,
          label: `${daysRemaining}d`,
        };
      case 'warning': // 3-7 days
        return {
          color: theme.palette.warning.main,
          backgroundColor: theme.palette.warning.light,
          icon: <ScheduleIcon sx={{ fontSize: 14 }} />,
          label: `${daysRemaining}d`,
        };
      default: // 7+ days
        return {
          color: theme.palette.info.main,
          backgroundColor: theme.palette.info.light,
          icon: <ScheduleIcon sx={{ fontSize: 14 }} />,
          label: `${t('trial.trial')} ${daysRemaining}d`,
        };
    }
  };

  const badgeProps = getBadgeProps();

  return (
    <Chip
      className={className}
      size={size}
      icon={badgeProps.icon}
      label={badgeProps.label}
      clickable={clickable}
      onClick={onClick}
      sx={{
        height: size === 'small' ? 24 : 32,
        fontSize: size === 'small' ? '0.75rem' : '0.8125rem',
        fontWeight: 600,
        color: badgeProps.color,
        backgroundColor: badgeProps.backgroundColor,
        border: `1px solid ${badgeProps.color}`,
        '& .MuiChip-icon': {
          color: badgeProps.color,
        },
        ...(clickable && {
          '&:hover': {
            backgroundColor: badgeProps.color,
            color: 'white',
            '& .MuiChip-icon': {
              color: 'white',
            },
          },
          cursor: 'pointer',
        }),
      }}
    />
  );
};

export default TrialBadge;
